// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createHook,
	createContainer,
} from '@atlassian/react-sweet-state';

type State = {
	isOpen: boolean;
	// analogous to a react key, this is used to force a re-render of the add gadget side panel
	// when the user clicks on the add gadget button so that the focus can be moved to the panel content
	//
	// a value of = 0 will trigger the focus on the trigger
	// a value of > 0 will trigger the focus on the panel
	focusKey: number;
	panelId: string | null;
	isTriggerFocused: boolean;
};

const initialState: State = {
	isOpen: false,
	focusKey: 0,
	panelId: null,
	isTriggerFocused: false,
};

const openPanel =
	(panelId: string): Action<State> =>
	({ setState, getState }) => {
		if (getState().isOpen === false) {
			setState({
				isOpen: true,
				focusKey: getState().focusKey + 1,
				panelId,
				isTriggerFocused: false,
			});
		} else {
			setState({
				// if the panel is already open, we only want to focus it
				// this scenario occurs when the panel is open by default
				// and the user clicks on the add gadget button
				// update key to trigger re-focusing of the side panel
				focusKey: getState().focusKey + 1,
			});
		}
	};

const closePanel =
	(): Action<State> =>
	({ setState, getState }) => {
		if (getState().isOpen === true) {
			setState({ ...initialState, isTriggerFocused: true });
		}
	};

const focusTrigger =
	(focusTriggerCallback: () => void): Action<State> =>
	({ setState, getState }) => {
		if (getState().isTriggerFocused) {
			focusTriggerCallback();
		}
		setState({ isTriggerFocused: false });
	};

const focusPanel =
	(focusPanelCallback: () => void): Action<State> =>
	({ getState }) => {
		if (getState().focusKey > 0) {
			focusPanelCallback();
		}
	};

const Store = createStore({
	initialState,
	actions: {
		openPanel,
		closePanel,
		focusTrigger,
		focusPanel,
	},
	name: 'right-side-panel',
});

type Actions = {
	openPanel: typeof openPanel;
	closePanel: typeof closePanel;
	focusTrigger: typeof focusTrigger;
	focusPanel: typeof focusPanel;
};

const useRightPanel = createHook(Store);

const useRightPanelActions = createHook(Store, {
	selector: null,
});

export const useRightPanelToggle = () => {
	const [, actions] = useRightPanelActions();
	return {
		onCloseRightPanel: actions.closePanel,
		onOpenRightPanel: actions.openPanel,
		onFocusTrigger: actions.focusTrigger,
		onFocusPanel: actions.focusPanel,
	};
};

export const useRightPanelState = () => useRightPanel()[0];

export const RightPanelContainer = createContainer<
	State,
	Actions,
	{
		isOpenByDefault: boolean;
		defaultPanelId?: string;
	}
>(Store, {
	onInit:
		() =>
		({ setState }, { isOpenByDefault, defaultPanelId }) => {
			if (isOpenByDefault === true && defaultPanelId != null) {
				setState({ isOpen: true, panelId: defaultPanelId });
			}
		},
});
